import { useTranslation } from 'react-i18next'

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '#/shared/ui/dialog'
import { CloseIcon } from '#/shared/ui/icons'
import type { MarketRateRow, MarketRateStats } from '../model/types'
import { MarketRateSummary } from './market-rate-summary'
import { MarketRateSummarySkeleton } from './market-rate-summary-skeleton'
import { MarketRateTable } from './market-rate-table'

interface MarketRateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  stats?: MarketRateStats
  rows?: MarketRateRow[]
  isLoading?: boolean
  errorMessage?: string
  onUseRate?: (row: MarketRateRow) => void
}

export function MarketRateDialog({
  open,
  onOpenChange,
  stats,
  rows = [],
  isLoading = false,
  errorMessage,
  onUseRate,
}: MarketRateDialogProps) {
  const { t } = useTranslation()

  const handleUseRate = (row: MarketRateRow) => {
    onUseRate?.(row)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <header className="flex h-13 shrink-0 items-center justify-between border-b border-gray-200 px-5">
          <DialogTitle className="text-base font-semibold text-gray-500">
            {t('table.marketRate.title')}
          </DialogTitle>
          <DialogClose
            aria-label={t('table.marketRate.close')}
            className="flex size-8 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-50"
          >
            <CloseIcon className="size-3.5" />
          </DialogClose>
        </header>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <SummarySection
            isLoading={isLoading}
            stats={stats}
            errorMessage={errorMessage}
          />
          <MarketRateTable
            rows={rows}
            isLoading={isLoading}
            onUseRate={handleUseRate}
          />
        </div>

        <footer className="flex shrink-0 items-center justify-between border-t border-gray-200 bg-gray-100 px-5 py-3.5">
          <div className="flex items-center gap-1.5">
            <span className="size-3 rounded-full bg-gray-300" aria-hidden />
            <span className="text-xs text-gray-500">
              {t('table.marketRate.footer')}
            </span>
          </div>
        </footer>
      </DialogContent>
    </Dialog>
  )
}

function SummarySection({
  isLoading,
  stats,
  errorMessage,
}: {
  isLoading: boolean
  stats?: MarketRateStats
  errorMessage?: string
}) {
  if (isLoading) return <MarketRateSummarySkeleton />
  if (errorMessage) return <SummaryError message={errorMessage} />
  return <MarketRateSummary stats={stats} />
}

function SummaryError({ message }: { message: string }) {
  return (
    <div className="flex items-center border-b border-gray-200 bg-red-50 px-5 py-4">
      <span className="text-xs text-destructive">{message}</span>
    </div>
  )
}
